import { useEffect, useRef, useState } from "preact/hooks";
import { Button, Meter, Scope } from "../../components";
import { BREATH, VOWEL_HOLD_MS, VOWEL_ROUNDS, VOWELS } from "../../content/drills";
import { CLARITY } from "../../engine/clarity";
import { createOnsetDetector, isVoiced, type MicSession } from "../../audio/mic";
import { recordBreathPB, recordPhonationPB, profile } from "../../state/store";
import type { MicState } from "../useMic";

type SustainProps = {
  drillId: "vowel" | "breath";
  onDone: (clarityBase: number, label: string) => void;
  mic: MicSession | null;
  micState: MicState;
  micThreshold: number;
};

export function Sustain({ drillId, onDone, mic, micState, micThreshold }: SustainProps) {
  const micReady = micState === "ready" ? mic : null;
  return drillId === "vowel" ? (
    <VowelStage onDone={onDone} mic={micReady} micThreshold={micThreshold} />
  ) : (
    <BreathStage onDone={onDone} mic={micReady} micThreshold={micThreshold} />
  );
}

function VowelStage({ onDone, mic, micThreshold }: { onDone: (c: number, l: string) => void; mic: MicSession | null; micThreshold: number }) {
  const steps = Array.from({ length: VOWEL_ROUNDS * VOWELS.length }, (_, i) => VOWELS[i % VOWELS.length]!);
  const [i, setI] = useState(0);
  const [progress, setProgress] = useState(0);
  const [liveLevel, setLiveLevel] = useState(0);
  const streakRef = useRef({ current: 0, max: 0, lastVoicedAt: 0 });

  useEffect(() => {
    setProgress(0);
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(1, elapsed / VOWEL_HOLD_MS));
      if (elapsed >= VOWEL_HOLD_MS) {
        clearInterval(tick);
        if (i + 1 >= steps.length) {
          const maxSec = streakRef.current.max / 1000;
          if (mic && maxSec > 0) recordPhonationPB(Math.round(maxSec));
          onDone(CLARITY.warmup, "Vowel Stretch");
        } else setI((n) => n + 1);
      }
    }, 50);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, [i]);

  // tracks the longest continuous voiced stretch across the whole drill (all
  // 10 vowel steps) — pauses under ~300ms don't break the streak, since a
  // breath between vowels shouldn't zero out a real hold.
  useEffect(() => {
    if (!mic) return;
    let raf = 0;
    const start = Date.now();
    function poll() {
      const now = Date.now();
      const s = streakRef.current;
      if (isVoiced(mic!, micThreshold)) {
        s.current += now - (s.lastVoicedAt || now);
        s.lastVoicedAt = now;
        s.max = Math.max(s.max, s.current);
      } else if (now - s.lastVoicedAt > 300) {
        s.current = 0;
        s.lastVoicedAt = now;
      }
      setLiveLevel(mic!.rms());
      void start;
      raf = requestAnimationFrame(poll);
    }
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, [mic, micThreshold]);

  const v = steps[i]!;
  const meterValue = mic ? Math.min(1, liveLevel / Math.max(0.05, micThreshold * 2)) : progress;

  return (
    <div class="stage stage-sustain">
      <p class="stage-kicker">Say it long and loud</p>
      <div class="stage-vowel-row">
        <Meter value={meterValue} height={120} />
        <div class="display stage-hero">{v.letter}</div>
      </div>
      {mic && <Scope getTimeDomain={() => mic.timeDomain()} width={200} height={48} />}
      <p class="mono stage-say">"{v.say}"</p>
      <p class="stage-detail">{v.hint}</p>
    </div>
  );
}

function BreathStage({ onDone, mic, micThreshold }: { onDone: (c: number, l: string) => void; mic: MicSession | null; micThreshold: number }) {
  const [phase, setPhase] = useState<"inhale" | "count">("inhale");
  const [n, setN] = useState(0);
  const best = profile.value.bests.breathCount;

  useEffect(() => {
    if (phase !== "inhale") return;
    const t = setTimeout(() => setPhase("count"), BREATH.inhaleMs);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "count" || !mic) return;
    const detect = createOnsetDetector(micThreshold, 250);
    let raf = 0;
    function poll() {
      const now = performance.now();
      if (detect(mic!, now)) setN((x) => x + 1);
      raf = requestAnimationFrame(poll);
    }
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, [phase, mic, micThreshold]);

  function finish() {
    const isPB = recordBreathPB(n);
    onDone(CLARITY.warmup + (isPB ? CLARITY.phonationPB : 0), "Breath Ladder");
  }

  if (phase === "inhale") {
    return (
      <div class="stage stage-sustain">
        <p class="stage-kicker">Inhale…</p>
        <div class="display stage-hero-sm">BREATHE</div>
      </div>
    );
  }

  return (
    <div class="stage stage-sustain">
      <p class="stage-kicker">
        {mic ? "Count aloud — the mic is counting for you" : "Count aloud — tap for every number"}
      </p>
      <p class="mono stage-detail">Record to beat: {best || "—"}</p>
      <button
        class="drum"
        type="button"
        aria-label={mic ? `Count: ${n}, auto-detected` : "Tap to count one more number"}
        onClick={mic ? undefined : () => setN((x) => x + 1)}
      >
        <span class="mono display stage-hero" aria-hidden="true">
          {n}
        </span>
      </button>
      <Button variant="ghost" onClick={finish}>
        I'm out of air
      </Button>
    </div>
  );
}
